/* theiro core: lights switch, glow stage, day/night compare slider, delivery estimate,
   size guide dialog, shared helpers (window.theiroUtil). Loaded deferred on every page. */
(() => {
  const doc = document;
  const root = doc.documentElement;
  const cfg = window.theiro || {};
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- helpers ---------- */
  const emit = (name, detail = {}) => {
    doc.dispatchEvent(new CustomEvent(name, { detail }));
    try { window.Shopify?.analytics?.publish?.(name.replace(/[:-]/g, '_'), detail); } catch (e) { /* analytics optional */ }
  };
  const safeStore = (store, key, value) => {
    try {
      if (value === undefined) return store.getItem(key);
      if (value === null) store.removeItem(key); else store.setItem(key, value);
    } catch (e) { /* storage blocked (private mode) */ }
    return null;
  };
  const formatMoney = (cents) => {
    const fmt = cfg.moneyFormat || '${{amount}}';
    const value = (Number(cents) || 0) / 100;
    const withDelims = (n, d, t, s) => {
      const [i, f] = n.toFixed(d).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (f ? s + f : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
      if (k === 'amount_no_decimals') return withDelims(value, 0, ',', '.');
      if (k === 'amount_with_comma_separator') return withDelims(value, 2, '.', ',');
      if (k === 'amount_no_decimals_with_comma_separator') return withDelims(value, 0, '.', ',');
      return withDelims(value, 2, ',', '.');
    });
  };
  const announce = (msg) => {
    const live = doc.getElementById('tg-live');
    if (!live) return;
    live.textContent = '';
    setTimeout(() => { live.textContent = msg; }, 30);
  };
  window.theiroUtil = { emit, safeStore, formatMoney, announce };

  /* ---------- lights switch ---------- */
  let switching = false;
  const applyLights = (mode) => {
    root.setAttribute('data-lights', mode);
    root.dataset.mascotMode = mode === 'on' ? 'day' : 'night';
    doc.querySelector('meta[name="theme-color"]')?.setAttribute('content', mode === 'on' ? '#F3F6FB' : '#0A0F24');
    doc.querySelectorAll('[data-lights-toggle]').forEach((btn) => {
      btn.setAttribute('aria-checked', mode === 'out' ? 'true' : 'false');
    });
    window.Mascot?.mode?.(mode === 'on' ? 'day' : 'night');
  };
  const setLights = (mode, source = 'toggle') => {
    if (switching || (mode !== 'on' && mode !== 'out')) return;
    const dimmer = doc.querySelector('.lights-dimmer');
    const finish = () => {
      applyLights(mode);
      safeStore(localStorage, 'theiro-lights', mode);
      announce(mode === 'out' ? 'Lights out. The ball is glowing.' : 'Lights on. Daylight view.');
      emit('theiro:lights', { state: mode, source });
      emit('glow:daynight', { mode: mode === 'on' ? 'day' : 'night' });
      if (mode === 'out') chargeStages();
    };
    if (!dimmer || reduceMotion.matches) { finish(); return; }
    switching = true;
    dimmer.classList.toggle('is-day', mode === 'on');
    const frames = mode === 'out'
      ? [{ opacity: 0 }, { opacity: 0.9, offset: 0.17 }, { opacity: 0.3, offset: 0.26 }, { opacity: 1, offset: 0.37 }, { opacity: 1, offset: 0.5 }, { opacity: 0 }]
      : [{ opacity: 0 }, { opacity: 1, offset: 0.33 }, { opacity: 1, offset: 0.45 }, { opacity: 0 }];
    const anim = dimmer.animate(frames, { duration: mode === 'out' ? 700 : 600, easing: 'linear' });
    setTimeout(finish, mode === 'out' ? 300 : 250);
    anim.onfinish = () => { switching = false; };
  };
  doc.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-lights-toggle]');
    if (!btn) return;
    e.preventDefault();
    setLights(root.getAttribute('data-lights') === 'on' ? 'out' : 'on');
  });
  applyLights(root.getAttribute('data-lights') === 'on' ? 'on' : 'out');

  /* ---------- glow stage: breathe only while visible (max 2 live) ---------- */
  const live = new Set();
  const stageIO = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const el = entry.target;
      if (entry.isIntersecting && live.size < 2) { live.add(el); el.classList.add('is-live'); }
      if (!entry.isIntersecting) { live.delete(el); el.classList.remove('is-live'); }
    });
  }, { threshold: 0.2 });
  const initStages = (scope = doc) => scope.querySelectorAll('.glow-stage').forEach((el) => stageIO.observe(el));
  function chargeStages() {
    doc.querySelectorAll('.glow-stage').forEach((el) => {
      el.classList.add('is-charged');
      setTimeout(() => el.classList.remove('is-charged'), 1500);
    });
  }
  doc.addEventListener('click', (e) => {
    const charge = e.target.closest('[data-charge]');
    if (!charge) return;
    chargeStages();
    navigator.vibrate?.(12);
    announce('Charged. It glows brightest for the first minutes.');
    emit('theiro:charge', {});
  });

  /* ---------- day/night compare slider ---------- */
  const initCompare = (scope = doc) => {
    scope.querySelectorAll('[data-tg-compare]').forEach((box) => {
      if (box.dataset.ready) return;
      box.dataset.ready = '1';
      const range = box.querySelector('input[type="range"]');
      const labels = box.querySelectorAll('.tg-compare__label');
      const setPos = (v) => {
        box.style.setProperty('--pos', `${v}%`);
        range.setAttribute('aria-valuetext', `${Math.round(v)}% daylight`);
        labels[0] && (labels[0].style.opacity = v < 15 ? 0 : 1);
        labels[1] && (labels[1].style.opacity = v > 85 ? 0 : 1);
      };
      range.addEventListener('input', () => setPos(range.value));
      /* Any user input stops the hint animation, so it never overrides arrow keys. */
      ['pointerdown', 'keydown', 'focus'].forEach((t) => range.addEventListener(t, () => { box.dataset.touched = '1'; }, { once: true }));
      if (reduceMotion.matches) return;
      const hintIO = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting || box.dataset.touched) return;
        hintIO.disconnect();
        const keys = [50, 30, 70, 50];
        const start = performance.now();
        const step = (now) => {
          if (box.dataset.touched) return;
          const t = Math.min(1, (now - start) / 1800);
          const seg = Math.min(2, Math.floor(t * 3));
          const local = t * 3 - seg;
          const ease = local < 0.5 ? 2 * local * local : 1 - Math.pow(-2 * local + 2, 2) / 2;
          const v = keys[seg] + (keys[seg + 1] - keys[seg]) * ease;
          range.value = v;
          setPos(v);
          if (t < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }, { threshold: 0.5 });
      hintIO.observe(box);
    });
  };

  /* ---------- delivery estimate (client-side; Liquid output is cached) ---------- */
  const delivery = cfg.delivery || {};
  const holidays = String(delivery.holidays || '').split(',').map((s) => s.trim()).filter(Boolean);
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const addBusinessDays = (from, n) => {
    const d = new Date(from);
    let k = 0;
    while (k < n) {
      d.setDate(d.getDate() + 1);
      const w = d.getDay();
      if (w !== 0 && w !== 6 && !holidays.includes(ymd(d))) k += 1;
    }
    return d;
  };
  const parseDay = (s) => (s && /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T23:59:59`) : null);
  const initDelivery = (scope = doc) => {
    const els = scope.querySelectorAll('[data-tg-delivery]');
    if (!els.length) return;
    const now = new Date();
    const fmt = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const a = addBusinessDays(now, Number(delivery.min) || 5);
    const b = addBusinessDays(now, Number(delivery.max) || 7);
    const isUS = (delivery.country || 'US') === 'US';
    const cutoff = parseDay(isUS ? delivery.cutoffUsa : delivery.cutoffWorld);
    const express = parseDay(delivery.cutoffUsaExpress);
    const shortFmt = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    let xmas = '';
    const nov15 = new Date(now.getFullYear(), 10, 15);
    if (now >= nov15 && cutoff) {
      if (now <= cutoff) xmas = `Order by ${shortFmt.format(cutoff)} for estimated Christmas delivery.`;
      else if (isUS && express && now <= express) xmas = `Need it by Christmas? Choose Express at checkout by ${shortFmt.format(express)}.`;
    }
    els.forEach((el) => {
      const range = el.querySelector('[data-tg-delivery-range]');
      if (range) range.textContent = `${fmt.format(a)} – ${fmt.format(b)}`;
      const x = el.querySelector('[data-tg-delivery-xmas]');
      if (x) { x.textContent = xmas; x.hidden = !xmas; }
    });
  };

  /* ---------- size guide dialog + quiz ---------- */
  const pickSize = (value) => {
    const input = [...doc.querySelectorAll('variant-selects input[type="radio"]')].find((i) => i.value === value);
    if (input && !input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    emit('glow:size-pick', { value });
  };
  window.theiroUtil.pickSize = pickSize;
  doc.addEventListener('click', (e) => {
    const open = e.target.closest('[data-tg-dialog-open]');
    if (open) {
      const dlg = doc.getElementById(open.dataset.tgDialogOpen);
      if (dlg?.showModal) { dlg.showModal(); emit('size_guide_open', {}); }
      return;
    }
    const close = e.target.closest('[data-tg-dialog-close]');
    if (close) { close.closest('dialog')?.close(); return; }
    const pick = e.target.closest('[data-size-pick]');
    if (pick) {
      pickSize(pick.dataset.sizePick);
      pick.closest('dialog')?.close();
      announce(`${pick.dataset.sizePick} selected`);
    }
  });
  doc.addEventListener('click', (e) => {
    if (e.target.matches('dialog.tg-dialog')) e.target.close();
  });
  doc.addEventListener('click', (e) => {
    const link = e.target.closest('[data-tg-open-game]');
    if (!link) return;
    const details = doc.querySelector('#glow-game details');
    if (details) details.open = true;
  });

  /* ---------- mobile menu: same-page anchors (/#bundles) ---------- */
  doc.addEventListener('click', (e) => {
    const a = e.target.closest('.menu-drawer a[href*="#"]');
    if (!a) return;
    const url = new URL(a.href, location.href);
    if (url.pathname !== location.pathname || !url.hash) return;
    const target = doc.getElementById(decodeURIComponent(url.hash.slice(1)));
    const drawer = a.closest('header-drawer');
    if (!target || !drawer?.closeMenuDrawer) return;
    e.preventDefault();
    drawer.closeMenuDrawer(e);
    setTimeout(() => {
      history.pushState(null, '', url.hash);
      target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }, 0);
  });

  /* ---------- lazy videos: play only while visible ---------- */
  const videoIO = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const v = entry.target;
      if (entry.isIntersecting) {
        if (!v.src && v.dataset.src) v.src = v.dataset.src;
        if (!reduceMotion.matches && !v.dataset.userPaused) v.play?.().catch(() => {});
      } else {
        v.pause?.();
      }
    });
  }, { threshold: 0.25 });
  const saveData = navigator.connection?.saveData;
  const setVideoButton = (btn, playing) => {
    btn.textContent = playing ? 'Pause' : 'Play';
    btn.setAttribute('aria-pressed', playing ? 'false' : 'true');
  };
  const initVideos = (scope = doc) => {
    scope.querySelectorAll('video[data-tg-autoplay]').forEach((v) => {
      const btn = v.closest('.tg-video')?.querySelector('[data-tg-video-toggle]');
      /* No autoplay for reduced motion / Save-Data: the shopper starts it with the button. */
      if (saveData || reduceMotion.matches) { if (btn) setVideoButton(btn, false); return; }
      videoIO.observe(v);
    });
  };
  /* WCAG 2.2.2: looping video must be pausable. */
  doc.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-tg-video-toggle]');
    if (!btn) return;
    const v = btn.closest('.tg-video')?.querySelector('video');
    if (!v) return;
    if (!v.src && v.dataset.src) v.src = v.dataset.src;
    if (v.paused) {
      delete v.dataset.userPaused;
      v.play().catch(() => {});
      setVideoButton(btn, true);
    } else {
      v.pause();
      v.dataset.userPaused = '1';
      setVideoButton(btn, false);
    }
  });

  const init = (scope) => { initStages(scope); initCompare(scope); initDelivery(scope); initVideos(scope); };
  init(doc);
  doc.addEventListener('shopify:section:load', (e) => init(e.target));
  doc.addEventListener('shopify:section:unload', (e) => e.target.querySelectorAll('.glow-stage').forEach((el) => {
    live.delete(el);
    stageIO.unobserve(el);
  }));
  window.theiroUtil.initDelivery = initDelivery;
})();
